import { html } from '../core/html.js';
import { t } from '../core/i18n.js';
import { page, breadcrumbs } from '../components/chrome.js';
import { ctaSection } from '../components/ctaSection.js';
import { artwork } from '../components/artwork.js';
import { icon } from '../components/icon.js';
import { artworkManifest } from '../data/artwork.js';
import { releasedGames, upcomingGames } from '../data/games.js';
import { site } from '../data/site.js';

const pressGames = [...releasedGames, ...upcomingGames];

const pageHead = () => html`
  <section class="pagehead" aria-labelledby="press-title">
    <div class="shell">
      ${breadcrumbs('nav.press')}
      <div class="pagehead__grid">
        <div>
          <p class="kicker">${t('press.label')}</p>
          <h1 class="pagehead__title" id="press-title">${t('press.title')}</h1>
          <p class="pagehead__lede">${t('press.lede')}</p>
        </div>
        <aside class="pagehead__aside">
          <p class="kicker">${t('press.contact.label')}</p>
          <p class="pagehead__note">
            <strong>${site.director}</strong><br />
            ${t('press.contact.role')}<br />
            <a class="link" href="mailto:${site.pressEmail}">${site.pressEmail}</a>
          </p>
          <p class="pagehead__meta">${t('press.updated')} · ${site.address.city}</p>
        </aside>
      </div>
    </div>
  </section>
`;

/* Values are read at render time so they follow a language switch. */
const facts = () => [
  { labelKey: 'press.fact.location.label', value: t('press.fact.location.value') },
  { labelKey: 'press.fact.management.label', value: site.director },
  {
    labelKey: 'press.fact.released.label',
    value: t('press.fact.released.value', { count: releasedGames.length }),
  },
  {
    labelKey: 'press.fact.upcoming.label',
    value: t('press.fact.upcoming.value', { count: upcomingGames.length }),
  },
];

const portrait = () => html`
  <section class="section press" aria-labelledby="portrait-title">
    <div class="shell">
      <div class="section-label">
        <span class="section-label__num">01</span>
        <span>${t('press.portrait.label')}</span>
      </div>
      <div class="section-head">
        <h2 class="section-head__title" id="portrait-title">${t('press.portrait.title')}</h2>
        <p class="section-head__lede">${t('press.portrait.lede')}</p>
      </div>

      <div class="press__grid">
        <div class="press__body">
          <p>${t('press.portrait.text1')}</p>
          <p>${t('press.portrait.text2', { director: site.director })}</p>

          <div class="press__boilerplate">
            <p class="kicker">${t('press.boilerplate.label')}</p>
            <h3>${t('press.boilerplate.title')}</h3>
            <p>${t('press.boilerplate.text', { released: releasedGames.length, director: site.director })}</p>
          </div>
        </div>

        <dl class="press-facts">
          ${facts().map(
            (fact) => html`
              <div class="press-fact">
                <dt class="press-fact__label">${t(fact.labelKey)}</dt>
                <dd class="press-fact__value">${fact.value}</dd>
              </div>
            `,
          )}
        </dl>
      </div>
    </div>
  </section>
`;

const pressKit = () => html`
  <section class="section section--muted press-kit" aria-labelledby="kit-title">
    <div class="shell">
      <div class="section-label">
        <span class="section-label__num">02</span>
        <span>${t('press.kit.label')}</span>
      </div>
      <div class="section-head">
        <h2 class="section-head__title" id="kit-title">${t('press.kit.title')}</h2>
        <p class="section-head__lede">${t('press.kit.lede')}</p>
      </div>

      <ul class="press-kit__grid">
        <li class="press-kit__item press-kit__item--logo">
          <div class="press-kit__media">
            <img
              src="/assets/images/NETBYTE-01.png"
              alt="${t('press.kit.logoAlt')}"
              width="1098"
              height="228"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div class="press-kit__body">
            <p class="press-kit__name">
              ${t('press.kit.logo')}
              <span class="press-kit__format">PNG</span>
            </p>
            <a class="press-kit__download" href="/assets/images/NETBYTE-01.png" download="netbyte-logo.png">
              ${t('press.kit.download')}${icon('arrowRight', { size: 14 })}
            </a>
          </div>
        </li>

        ${pressGames.map((game) => {
          const title = t(`game.${game.slug}.title`);
          const source = artworkManifest[game.art].source;
          const format = source.split('.').pop().toUpperCase();

          return html`
            <li class="press-kit__item">
              <div class="press-kit__media">
                ${artwork(game.art, title, {
                  sizes: '(max-width: 640px) 100vw, (max-width: 1000px) 45vw, 30vw',
                })}
              </div>
              <div class="press-kit__body">
                <p class="press-kit__name">
                  ${title}
                  <span class="press-kit__format">${format}</span>
                </p>
                <a class="press-kit__download" href="/assets/images/${source}" download="${source}">
                  ${t('press.kit.download')}${icon('arrowRight', { size: 14 })}
                </a>
              </div>
            </li>
          `;
        })}
      </ul>

      <p class="press-kit__note">
        ${t('press.kit.note')}
        <a href="mailto:${site.pressEmail}">${site.pressEmail}</a>
      </p>
    </div>
  </section>
`;

export const PressPage = () => page(html`
  ${pageHead()}${portrait()}${pressKit()}
  ${ctaSection({
    labelKey: 'press.cta.label',
    titleKey: 'press.cta.title',
    ledeKey: 'press.cta.lede',
    buttonKey: 'press.cta.button',
    email: site.pressEmail,
    href: `mailto:${site.pressEmail}`,
  })}
`);
